import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Select, Space, Table, Tag } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const PmvReliabilityFaultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | undefined>('ACTIVE');

  const q = useQuery({
    queryKey: ['pmv-reliability-faults', status],
    queryFn: () =>
      pmvService.listReliabilityFaults({
        status,
        limit: 100,
      }),
  });

  const items = q.data?.items ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Fault Code Aggregates"
        subtitle="Occurrence counts aggregated — disappearance is NO_LONGER_OBSERVED, not auto-resolved"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/reliability/control-tower')}>
              Control Tower
            </Button>
            <Button onClick={() => navigate('/asset/pmv/reliability')}>Health Register</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/attention')}>Attention</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/failures')}>Failures</Button>
          </Space>
        }
        extra={
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 200 }}
            value={status}
            onChange={setStatus}
            options={['ACTIVE', 'NO_LONGER_OBSERVED'].map((v) => ({ value: v, label: v }))}
          />
        }
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Fault storms increment count on one aggregate row — Connected Fleet retains raw evidence."
      />
      {!q.isLoading && items.length === 0 ? (
        <Empty description="No fault aggregates for these filters" />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1000 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Equipment',
              dataIndex: 'equipment_label',
              width: 200,
              ellipsis: true,
              render: (v, r) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}`)}
                  >
                    {v || 'Equipment'}
                  </Button>
                </div>
              ),
            },
            { title: 'Code', dataIndex: 'code', width: 120, ellipsis: true },
            {
              title: 'System',
              dataIndex: 'system_code',
              width: 140,
              ellipsis: true,
              render: (v) => v || 'Unmapped',
            },
            {
              title: 'Count',
              dataIndex: 'occurrence_count',
              width: 90,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 160,
              render: (v) => <Tag>{v}</Tag>,
            },
            {
              title: 'Last seen',
              dataIndex: 'last_observed_at',
              width: 170,
              render: (v) => (v ? new Date(v).toLocaleString() : '—'),
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvReliabilityFaultsPage;
